if (!customElements.get('voicsky-switch-card')) {
  customElements.define('voicsky-switch-card', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (el, type, fn) => el?.addEventListener(type, fn, { signal: this.abort.signal });
      this.audio = this.querySelector('audio');
      this.video = this.querySelector('video');
      this.button = this.querySelector('.vsc__play');
      this.videoButton = this.querySelector('.vsc__video-toggle');
      this.seek = this.querySelector('.vsc__seek');
      this.currentLabel = this.querySelector('[data-current-time]');
      this.durationLabel = this.querySelector('[data-duration]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.videoPaused = this.motion.matches;
      on(this.button, 'click', async () => {
        if (!this.audio) return;
        this.loadWave();
        clearTimeout(this.resetProgressTimer);
        if (!this.audio.paused) { this.audio.pause(); return; }
        document.querySelectorAll('voicsky-switch-card audio').forEach(audio => { if (audio !== this.audio) audio.pause(); });
        if (this.audio.ended) this.audio.currentTime = 0;
        this.querySelector('.vsc__status').textContent = '';
        try { await this.audio.play(); } catch (error) { if (error.name !== 'AbortError') this.audioError(); }
      });
      on(this.audio, 'play', () => { this.playing(true); this.animateProgress(); });
      on(this.audio, 'pause', () => { this.playing(false); cancelAnimationFrame(this.progressFrame); this.progress(); });
      on(this.audio, 'ended', () => {
        this.playing(false);
        cancelAnimationFrame(this.progressFrame);
        this.progress();
        clearTimeout(this.resetProgressTimer);
        this.resetProgressTimer = setTimeout(() => {
          if (this.audio?.ended) { this.audio.currentTime = 0; this.progress(); }
        }, 180);
      });
      on(this.audio, 'timeupdate', () => this.progress());
      on(this.audio, 'loadedmetadata', () => this.progress());
      on(this.audio, 'durationchange', () => this.progress());
      on(this.seek, 'input', () => {
        clearTimeout(this.resetProgressTimer);
        if (Number.isFinite(this.audio?.duration)) { this.audio.currentTime = this.audio.duration * Number(this.seek.value) / 100; this.progress(); }
      });
      this.progress();
      on(this.audio, 'error', () => this.audioError());
      on(this.videoButton, 'click', () => { this.videoPaused = !this.videoPaused; this.updateVideo(); });
      on(this.motion, 'change', () => { this.videoPaused = this.motion.matches; this.updateVideo(); });
      on(document, 'visibilitychange', () => { if (document.hidden) this.audio?.pause(); this.updateVideo(); });
      this.resize = new ResizeObserver(() => { this.updateVideo(); this.alignImage(); this.sizeWave(); });
      this.resize.observe(this);
      this.resize.observe(this.querySelector('.vsc__audio-progress'));
      this.sizeWave();
      this.titleGroup = this.querySelector('.vsc__titles');
      if (this.titleGroup) this.resize.observe(this.titleGroup);
      this.alignImage();
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.loadWave();
        if (!this.visible) this.audio?.pause();
        this.updateVideo();
      }, { threshold: .1 });
      this.intersection.observe(this);
    }
    sizeWave() {
      const wave = this.querySelector('.vsc__simple-wave');
      if (!wave) return;
      const count = Math.max(1, Math.floor((wave.clientWidth - 24 + 2) / 5));
      if (wave.children.length === count) return;
      const bars = Array.from({ length: count }, (_, i) => {
        const bar = document.createElement('span');
        bar.style.height = '3px';
        return bar;
      });
      wave.replaceChildren(...bars);
      this.drawWave();
      this.progress();
    }
    async loadWave() {
      if (!this.audio || this.waveLoading || this.wavePeaks) return;
      const Context = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      if (!Context) return;
      this.waveLoading = true;
      const signal = this.abort.signal;
      try {
        const response = await fetch(this.audio.currentSrc || this.audio.src, { signal });
        if (!response.ok) throw new Error('Audio unavailable');
        const context = new Context(1, 1, 44100);
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        if (signal.aborted) return;
        const count = Math.min(2048, buffer.length);
        const peaks = new Float32Array(count);
        for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
          const samples = buffer.getChannelData(channel);
          for (let i = 0; i < count; i++) {
            const start = Math.floor(i * samples.length / count);
            const end = Math.floor((i + 1) * samples.length / count);
            for (let j = start; j < end; j++) peaks[i] = Math.max(peaks[i], Math.abs(samples[j]));
          }
        }
        this.wavePeaks = peaks;
        this.drawWave();
      } catch (error) {
        if (!signal.aborted) this.querySelector('.vsc__simple-wave')?.setAttribute('data-wave-unavailable', '');
      } finally {
        this.waveLoading = false;
      }
    }
    drawWave() {
      if (!this.wavePeaks) return;
      const bars = [...this.querySelectorAll('.vsc__simple-wave span')];
      const peaks = bars.map((_, i) => {
        const start = Math.floor(i * this.wavePeaks.length / bars.length);
        const end = Math.max(start + 1, Math.floor((i + 1) * this.wavePeaks.length / bars.length));
        let peak = 0;
        for (let j = start; j < end; j++) peak = Math.max(peak, this.wavePeaks[j]);
        return peak;
      });
      const maximum = Math.max(...peaks, 0.001);
      bars.forEach((bar, i) => { bar.style.height = `${Math.max(4, Math.sqrt(peaks[i] / maximum) * 28)}px`; });
    }
    animateProgress() {
      cancelAnimationFrame(this.progressFrame);
      const tick = () => {
        this.progress();
        if (this.audio && !this.audio.paused && !this.audio.ended) this.progressFrame = requestAnimationFrame(tick);
      };
      tick();
    }
    alignImage() {
      const icon = this.querySelector('.vsc__icon');
      const title = this.querySelector('.vsc__titles');
      if (!icon || !title) return;
      const identity = this.querySelector('.vsc__identity');
      const requested = parseFloat(getComputedStyle(identity).getPropertyValue('--vsc-image-requested')) || 104;
      const size = Math.min(requested, identity.clientWidth * .48);
      const next = `${Math.max(48, size)}px`;
      if (identity.style.getPropertyValue('--vsc-image-size') !== next) identity.style.setProperty('--vsc-image-size', next);
    }
    playing(value) {
      this.toggleAttribute('data-playing', value);
      this.button.setAttribute('aria-label', `${value ? this.button.dataset.pause : this.button.dataset.play}: ${this.button.dataset.title}`);
    }
    progress() {
      const fraction = Number.isFinite(this.audio?.duration) && this.audio.duration > 0 ? this.audio.currentTime / this.audio.duration : 0;
      const duration = Number.isFinite(this.audio?.duration) ? this.audio.duration : 0;
      const format = value => duration > 0 && duration < 1 ? `${value.toFixed(1)}s` : `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;
      this.seek.disabled = duration <= 0;
      this.seek.value = fraction * 100;
      this.querySelectorAll('.vsc__simple-wave span').forEach((bar, i, bars) => bar.classList.toggle('is-played', fraction > i / bars.length));
      this.seek.style.setProperty('--progress', `${fraction * 100}%`);
      this.currentLabel.textContent = format(this.audio?.currentTime || 0);
      this.durationLabel.textContent = format(duration);
    }
    audioError() {
      this.playing(false);
      const status = this.querySelector('.vsc__status');
      status.textContent = status.dataset.error;
    }
    updateVideo() {
      if (!this.video) return;
      const shown = getComputedStyle(this.video.parentElement).display !== 'none';
      if (this.visible && shown && !document.hidden && !this.videoPaused) {
        if (!this.loaded) {
          this.video.querySelectorAll('source').forEach(source => { source.src = source.dataset.src; });
          this.video.load(); this.loaded = true;
        }
        this.video.play().catch(() => {});
      } else this.video.pause();
      this.videoButton.setAttribute('aria-label', this.videoPaused ? this.videoButton.dataset.play : this.videoButton.dataset.pause);
      this.videoButton.toggleAttribute('data-paused', this.videoPaused);
    }
    disconnectedCallback() {
      clearTimeout(this.resetProgressTimer);
      cancelAnimationFrame(this.progressFrame);
      this.audio?.pause(); this.video?.pause();
      this.abort?.abort(); this.abort = null;
      this.resize?.disconnect(); this.intersection?.disconnect();
    }
  });
}

if (!customElements.get('voicsky-switch-carousel')) {
  customElements.define('voicsky-switch-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.track = this.querySelector('.vsc__cards');
      this.cards = [...this.querySelectorAll('voicsky-switch-card')];
      this.pages = [...this.querySelectorAll('[data-page]')];
      this.desktop = matchMedia('(min-width:1200px)');
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.controller.signal });
      on(this, 'click', event => {
        const button = event.target.closest('[data-direction], [data-page]');
        if (!button) return;
        this.go(button.hasAttribute('data-page') ? Number(button.dataset.page) : this.index + Number(button.dataset.direction));
      });
      on(this.track, 'scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      });
      on(this.desktop, 'change', () => this.update());
      on(this, 'shopify:block:select', event => {
        const card = event.target.closest('voicsky-switch-card');
        if (card) this.go(this.cards.indexOf(card));
      });
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.update();
    }
    update() {
      this.active = this.desktop.matches && this.dataset.columns === '1';
      const origin = this.track.getBoundingClientRect().left;
      this.index = this.cards.reduce((best, card, i) => Math.abs(card.getBoundingClientRect().left - origin) < Math.abs(this.cards[best].getBoundingClientRect().left - origin) ? i : best, 0);
      this.pages.forEach((page, i) => page.setAttribute('aria-current', String(i === this.index)));
      const prev = this.querySelector('[data-direction="-1"]');
      const next = this.querySelector('[data-direction="1"]');
      if (prev) prev.disabled = this.index === 0;
      if (next) next.disabled = this.index === this.cards.length - 1;
      this.cards.forEach((card, i) => {
        card.inert = this.active && i !== this.index;
        if (card.inert) card.setAttribute('aria-hidden', 'true'); else card.removeAttribute('aria-hidden');
      });
    }
    go(index) {
      if (!this.active) return;
      const card = this.cards[Math.max(0, Math.min(index, this.cards.length - 1))];
      this.track.scrollTo({left:this.track.scrollLeft + card.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
    }
  });
}
